/**
 * Check-list de maintenance standardisée d'un GRV (CDC §7, `docs/DECISIONS.md` D78).
 *
 * Le modèle (les questions) vient du référentiel `checklist_question`, pas d'une saisie libre :
 * chaque nouvelle occurrence s'initialise avec une réponse vide par question active. Le cycle de
 * vie passe par trois statuts — brouillon (modifiable), terminée (verrouillée), validée
 * (confirmation finale, mêmes droits que le reste du domaine) — et chaque occurrence reste
 * consultable dans l'historique, jamais écrasée par la suivante.
 */
import { useEffect, useState } from "react";

import { ErreurApi } from "../../shared/api/erreurs";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { formaterDate } from "../../shared/composants/formatage";
import type { ChecklistEnregistree, EnregistrementChecklist, ReponseChecklist } from "../../shared/api/types";
import {
  useDemarrerChecklist,
  useEnregistrerBrouillonChecklist,
  useHistoriqueChecklists,
  useQuestionsChecklist,
  useTerminerChecklist,
  useValiderChecklist,
} from "./requetes";

const LIBELLE_STATUT: Record<string, string> = {
  brouillon: "Brouillon",
  terminee: "Terminée",
  validee: "Validée",
};

function reponsesVers(reponses: ReponseChecklist[]): Record<string, string | boolean | number | null> {
  return Object.fromEntries(reponses.map((r) => [r.code_question, r.reponse ?? null]));
}

export function OngletChecklist({
  numero,
  peutAdministrer,
}: {
  numero: string;
  peutAdministrer: boolean;
}) {
  const questions = useQuestionsChecklist();
  const historique = useHistoriqueChecklists(numero);
  const demarrer = useDemarrerChecklist(numero);
  const enregistrerBrouillon = useEnregistrerBrouillonChecklist(numero);
  const terminer = useTerminerChecklist(numero);
  const valider = useValiderChecklist(numero);

  const [vue, definirVue] = useState<"en_cours" | "historique">("en_cours");
  const [reponses, definirReponses] = useState<Record<string, string | boolean | number | null>>({});
  const [commentaire, definirCommentaire] = useState("");

  const occurrences = historique.data?.data ?? [];
  const derniere: ChecklistEnregistree | undefined = occurrences[0];

  // Réinitialise le formulaire à chaque fois que la check-list éditable change (nouveau brouillon
  // démarré, ou données rechargées après une sauvegarde) — pas à chaque frappe de l'utilisateur.
  // Se resynchroniser sur `derniere.reponses`/`commentaire` à chaque rendu écraserait la saisie en
  // cours dès la moindre revalidation de la requête, avant même que l'utilisateur ait sauvegardé.
  useEffect(() => {
    if (derniere?.statut === "brouillon") {
      definirReponses(reponsesVers(derniere.reponses ?? []));
      definirCommentaire(derniere.commentaire ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derniere?.checklist_id, derniere?.statut]);

  function construireCorps(): EnregistrementChecklist {
    return {
      reponses: Object.entries(reponses).map(([code_question, reponse]) => ({
        code_question,
        reponse,
      })),
      commentaire: commentaire || null,
    };
  }

  async function enregistrerLeBrouillon() {
    if (!derniere) return;
    await enregistrerBrouillon.mutateAsync({ checklistId: derniere.checklist_id, corps: construireCorps() });
  }

  async function terminerLaChecklist() {
    if (!derniere) return;
    await terminer.mutateAsync({ checklistId: derniere.checklist_id, corps: construireCorps() });
  }

  if (questions.isPending || historique.isPending) {
    return <Chargement libelle="Chargement de la check-list…" />;
  }
  if (questions.isError) {
    return <Erreur erreur={questions.error} reessayer={() => void questions.refetch()} />;
  }
  if (historique.isError) {
    return <Erreur erreur={historique.error} reessayer={() => void historique.refetch()} />;
  }

  const listeQuestions = questions.data?.data ?? [];
  const erreurMutation =
    demarrer.error ?? enregistrerBrouillon.error ?? terminer.error ?? valider.error;

  return (
    <div>
      <div className="groupe-bascule" role="group" aria-label="Vue">
        <button
          type="button"
          className={vue === "en_cours" ? "bouton-toggle bouton-toggle--actif" : "bouton-toggle"}
          aria-pressed={vue === "en_cours"}
          onClick={() => definirVue("en_cours")}
        >
          Check-list en cours
        </button>
        <button
          type="button"
          className={vue === "historique" ? "bouton-toggle bouton-toggle--actif" : "bouton-toggle"}
          aria-pressed={vue === "historique"}
          onClick={() => definirVue("historique")}
        >
          Historique
        </button>
      </div>

      {erreurMutation && (
        <p role="alert" className="formulaire-ligne__erreur">
          {erreurMutation instanceof ErreurApi ? erreurMutation.message : "L'opération a échoué."}
        </p>
      )}

      {vue === "en_cours" && (
        <>
          {!derniere && (
            <>
              <Vide libelle="Aucune check-list en cours pour ce GRV." />
              {peutAdministrer && (
                <button
                  type="button"
                  className="bouton-principal"
                  onClick={() => void demarrer.mutateAsync()}
                  disabled={demarrer.isPending}
                >
                  Démarrer une check-list
                </button>
              )}
            </>
          )}

          {derniere?.statut === "brouillon" && (
            <form onSubmit={(e) => e.preventDefault()}>
              <table className="tableau">
                <thead>
                  <tr>
                    <th scope="col">Question</th>
                    <th scope="col">Réponse</th>
                  </tr>
                </thead>
                <tbody>
                  {listeQuestions.map((question) => (
                    <tr key={question.code}>
                      <th scope="row">{question.libelle}</th>
                      <td>
                        <select
                          aria-label={question.libelle}
                          disabled={!peutAdministrer}
                          value={
                            reponses[question.code] === true
                              ? "oui"
                              : reponses[question.code] === false
                                ? "non"
                                : ""
                          }
                          onChange={(e) =>
                            definirReponses((valeurs) => ({
                              ...valeurs,
                              [question.code]:
                                e.target.value === "" ? null : e.target.value === "oui",
                            }))
                          }
                        >
                          <option value="">—</option>
                          <option value="oui">Conforme</option>
                          <option value="non">Non conforme</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <label htmlFor="checklist-observations">Observations / anomalies constatées</label>
              <textarea
                id="checklist-observations"
                disabled={!peutAdministrer}
                value={commentaire}
                onChange={(e) => definirCommentaire(e.target.value)}
              />

              {peutAdministrer && (
                <div className="formulaire-actions">
                  <button
                    type="button"
                    onClick={() => void enregistrerLeBrouillon()}
                    disabled={enregistrerBrouillon.isPending}
                  >
                    Enregistrer le brouillon
                  </button>
                  <button
                    type="button"
                    className="bouton-principal"
                    onClick={() => void terminerLaChecklist()}
                    disabled={terminer.isPending}
                  >
                    Terminer
                  </button>
                </div>
              )}
            </form>
          )}

          {derniere?.statut === "terminee" && (
            <div>
              <p className="carte__description">
                Check-list terminée le {formaterDate(derniere.termine_le)}, en attente de
                validation.
              </p>
              <TableauReponses reponses={derniere.reponses ?? []} questions={listeQuestions} />
              {derniere.commentaire && <p>Observations : {derniere.commentaire}</p>}
              {peutAdministrer && (
                <button
                  type="button"
                  className="bouton-principal"
                  onClick={() => void valider.mutateAsync(derniere.checklist_id)}
                  disabled={valider.isPending}
                >
                  Valider
                </button>
              )}
            </div>
          )}

          {derniere?.statut === "validee" && (
            <div>
              <p className="carte__description">
                Dernière check-list validée le {formaterDate(derniere.valide_le)} par{" "}
                {derniere.valide_par}.
              </p>
              <TableauReponses reponses={derniere.reponses ?? []} questions={listeQuestions} />
              {derniere.commentaire && <p>Observations : {derniere.commentaire}</p>}
              {peutAdministrer && (
                <button
                  type="button"
                  className="bouton-principal"
                  onClick={() => void demarrer.mutateAsync()}
                  disabled={demarrer.isPending}
                >
                  Démarrer une nouvelle check-list
                </button>
              )}
            </div>
          )}
        </>
      )}

      {vue === "historique" &&
        (occurrences.length === 0 ? (
          <Vide libelle="Aucune check-list enregistrée pour ce GRV." />
        ) : (
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Statut</th>
                <th scope="col">Créé le</th>
                <th scope="col">Créé par</th>
                <th scope="col">Terminé le</th>
                <th scope="col">Validé le</th>
                <th scope="col">Validé par</th>
              </tr>
            </thead>
            <tbody>
              {occurrences.map((occurrence) => (
                <tr key={occurrence.checklist_id}>
                  <td>
                    <span className="badge">
                      {LIBELLE_STATUT[occurrence.statut] ?? occurrence.statut}
                    </span>
                  </td>
                  <td>{formaterDate(occurrence.cree_le)}</td>
                  <td>{occurrence.cree_par ?? "—"}</td>
                  <td>{occurrence.termine_le ? formaterDate(occurrence.termine_le) : "—"}</td>
                  <td>{occurrence.valide_le ? formaterDate(occurrence.valide_le) : "—"}</td>
                  <td>{occurrence.valide_par ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </div>
  );
}

function TableauReponses({
  reponses,
  questions,
}: {
  reponses: ReponseChecklist[];
  questions: { code: string; libelle: string }[];
}) {
  const libelleDe = Object.fromEntries(questions.map((q) => [q.code, q.libelle]));
  return (
    <table className="tableau">
      <thead>
        <tr>
          <th scope="col">Question</th>
          <th scope="col">Réponse</th>
        </tr>
      </thead>
      <tbody>
        {reponses.map((reponse) => (
          <tr key={reponse.code_question}>
            <th scope="row">{libelleDe[reponse.code_question] ?? reponse.code_question}</th>
            <td>
              {reponse.reponse === true ? "Conforme" : reponse.reponse === false ? "Non conforme" : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
